import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { CaaSImageFilters } from "@typings/OptimisedImage";
import { getUrlQuery } from "@util/helpers";

/**
 * Formats a CaaS image URL with the given filters.
 * @param url - The URL of the image to format.
 * @param filters - The filters to apply to the image.
 * @returns The formatted URL.
 */
export const getFormattedCaaSImageUrl = (
  url?: string,
  filters?: CaaSImageFilters
) => {
  if (!url) return "";
  if (!filters) return url;

  const nextSafePreset = getNextSafePreset(filters.w ?? 0);
  const newFilters: CaaSImageFilters = { ...filters, w: nextSafePreset };
  const filtersObj = Object.entries(newFilters).map(([key, value]) => [
    key,
    value.toString(),
  ]);

  const linkParams = new URLSearchParams(filtersObj);
  const query = getUrlQuery(url, linkParams);

  return query ? `${url}${query}` : url;
};

/**
 * Preloads an image by creating a new Image object and setting its source to the specified URL.
 *
 * @param src - The URL of the image to preload.
 * @returns A promise that resolves when the image has finished loading, or rejects if an error occurs.
 */
export const preloadImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = reject;
  });
};

/**
 * Preloads an array of images by creating new Image objects and setting their sources to the specified URLs.
 *
 * @param imagesArray - An array of URLs of the images to preload.
 * @returns A promise that resolves when all the images have finished loading, or rejects if an error occurs.
 */
export const preloadImages = (imagesArray: string[] = []) => {
  const promises = imagesArray.map((src) => preloadImage(src));

  return Promise.all(promises);
};

/**
 * Gets the next larger preset for the given current preset.
 * @param current - The current preset.
 * @returns The next larger preset.
 */
export const getNextSafePreset = (current: number): ImagePresets => {
  const presets = Object.values(ImagePresets)
    .filter((v): v is number => typeof v === "number")
    .sort((a, b) => a - b);

  return presets.find((preset) => preset >= current) ?? ImagePresets.XLARGE;
};
